import { computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { bandColor, readVerdictField } from './CoverageBadge';
import {
  InlineTextAction,
  OriginalReference,
  SectionLabel,
} from './translationEditorPrimitives';
import { useOutsideAlerter } from './useClickOutside';

import type { CoverageVerdict } from '@src/types.ts/translation';

/** Breathing room kept between the popover and every viewport edge. */
const VIEWPORT_PADDING = 8;
/** Gap between the badge and the popover. */
const ANCHOR_GAP = 8;
/**
 * Floor for the height `size()` grants. If a score sits in a cramped strip we
 * would rather the popover overlap a little and stay readable than collapse to
 * a sliver.
 */
const MIN_POPOVER_HEIGHT = 160;

/**
 * PLAT-10268 (T8) — Atom 6 core. The click-through details popover for a
 * `CoverageBadge`. Opened from the badge, anchored to the badge's fragment.
 * Shows the AI coverage analysis (percent / status / missing / added /
 * explanation), the old (previous machine) translated text beside the current
 * text, and an Undo action. Deliberately NO close button (PLAT-11335): the
 * popover dismisses itself — hover-leave, Escape, click-outside — so a ✕ would
 * falsely signal it persists; close buttons are a modal affordance
 * (design-system "Close Buttons" lists only modals).
 *
 * Follows the `TranslatedPhraseEditorPopover` pattern: `position: fixed`,
 * placement computed once from the anchor rect, a native keydown trap so
 * transcript / workspace shortcuts don't fire while it is open, and close on
 * Escape / click-outside.
 *
 * Atom 7 adds a split control driven by a `SplitControls` interface (below); the
 * collapse / reapply store operations behind it are PLAT-10270's deliverable —
 * until it ships the workspace supplies a placeholder provider (predicates
 * false, no-op actions).
 *
 * PLAT-11335 reshaped the layout: the verdict word leads (the percent qualifies
 * it), only the analysis region scrolls so the actions stay reachable, section
 * labels come from the shared translation-editor primitives instead of a local
 * uppercased copy, and the Split / Undo-split / Redo-split trio collapsed into
 * one state-driven toggle.
 */

/**
 * PLAT-10268 (T8) — the contract PLAT-10270 fulfils. Defined here (the consumer
 * that renders the buttons) so the controls compile and the enabled/disabled
 * logic is real now. Keyed by a fragment's SEGMENT ID (its `parentId`, or the
 * fragment's own lead stable id when it has no segment yet) — matching the
 * store's existing segment-id split ops (`resplitOverrideSegment(lang,
 * segmentId)`) and PLAT-10270's provider (stable-id migration; DESIGN.md
 * "Identity model"). Shape is authoritative from DESIGN.md.
 */
export interface SplitControls {
  /** Is the segment currently split into sub-fragments? */
  isSplit(segmentId: string): boolean;
  /** Is a previously-computed split cached for this segment (available to redo)? */
  hasCachedSplit(segmentId: string): boolean;
  /** On-demand split (PLAT-10269 endpoint, wired by PLAT-10270). */
  splitSegment(segmentId: string): void | Promise<void>;
  /** Collapse the split back to one fragment — PLAT-10270. */
  undoSplit(segmentId: string): void;
  /** Reapply the cached split — PLAT-10270. */
  redoSplit(segmentId: string): void;
}

/**
 * PLAT-10268 (T8) — Atom 7. A safe, inert `SplitControls`: predicates report
 * "not split / nothing cached" (so the toggle offers Split Segment and calls
 * `splitSegment`) and the actions are no-ops. Used until
 * PLAT-10270 supplies the real collapse / reapply store operations, and as the
 * fallback when no provider is passed. Frozen so it can be shared safely.
 */
export const INERT_SPLIT_CONTROLS: SplitControls = Object.freeze({
  isSplit: () => false,
  hasCachedSplit: () => false,
  splitSegment: () => {},
  undoSplit: () => {},
  redoSplit: () => {},
});

export interface CoveragePopoverProps {
  /** The badge element the popover anchors below. */
  anchorElement: HTMLElement;
  /** The coverage analysis to display. */
  verdict: CoverageVerdict;
  /**
   * The old (previous) machine-translated text — the exact value the Undo
   * restores. For an override span this is the joined RETIRED machine fragments
   * the override superseded; for a plain phrase edit it is the per-phrase captured
   * baseline (`phrase.originalTranslatedValue ?? phrase.translatedValue`). The
   * host resolves it (see `AlignedBlocksView`). Undefined / equal-to-current when
   * no baseline is known — then the old-vs-current comparison just hides the old
   * row (Undo still reverts the store).
   */
  oldTranslatedValue?: string;
  /** The current (live) translated text of the covering fragment. */
  currentTranslatedValue: string;
  /**
   * Reverts the fragment's translation to the machine original
   * (`TranslationStore.revertTranslation`) and clears the cached verdict. The
   * host closes the popover after firing this.
   */
  onUndo: () => void;
  /** Closes the popover without reverting (Escape, click-outside, hover-leave). */
  onClose: () => void;
  /**
   * PLAT-10257 (A4) — the "Fix Translation" entry point. Opens the SAME
   * two-mode span editor (type a replacement | re-translate with an
   * instruction) for this segment's covered words. The popover contributes
   * ONLY the entry wiring: the host resolves the covered indices, computes the
   * overlap guard, opens the editor, and closes this popover. Optional — a
   * host that doesn't wire it renders no button (popover unchanged).
   */
  onFixTranslation?: () => void;
  /**
   * PLAT-10266 (Card 4) — is the anchored fragment STALE (its source words were
   * edited in the review's original column)? The resolve actions below only
   * render for a stale fragment. Optional — omitted (⇒ `false`) on every
   * existing call site, so the row never appears there (AC6 containment).
   */
  isStale?: boolean;
  /**
   * PLAT-10266 (Card 4) — the SECOND home for the two resolve actions (DESIGN §7
   * "two homes"). "Re-translate (machine)" — re-translate the stale fragment
   * from the machine (branching on override-vs-machine in the host workspace).
   * Entry wiring ONLY: the host (`AlignedBlocksView`) resolves the fragment's
   * lead, runs the resolve, and clears the amber; the popover just fires this.
   * Optional — a host that doesn't wire it renders no action (popover unchanged),
   * mirroring `onFixTranslation`.
   */
  onResolveRetranslate?: () => void;
  /**
   * PLAT-10266 (Card 4) — "Replace manually": open the shipped type-replacement
   * span editor on the stale fragment (DESIGN §7). Same entry-wiring-only,
   * optional contract as `onResolveRetranslate`; the action renders only when
   * this is wired.
   */
  onResolveManual?: () => void;
  /**
   * The HOVER BRIDGE (PLAT-10268, Denys 2026-07-14). The popover opens on badge
   * hover, so the pointer must be able to travel from the badge into this
   * popover without it closing underneath — otherwise Undo / Split are
   * unreachable. The owner cancels its pending close on enter and re-arms it on
   * leave. Optional: without them the popover is click-only, exactly as before.
   */
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
  /**
   * The open fragment's SEGMENT ID — the key the split controls are addressed
   * by (its `parentId`, or the fragment's own lead stable id when it has no
   * segment yet). Distinct from the verdict-context lead uuid the host keys the
   * popover-open state on; the two keys coexist by design (DESIGN.md decision C).
   */
  segmentId: string;
  /**
   * PLAT-10268 (T8) — Atom 7. The split-control provider. Drives the split
   * toggle's label and which tiling op its click runs. The workspace supplies a
   * placeholder until PLAT-10270 ships the real collapse / reapply operations.
   */
  splitControls: SplitControls;
}

const STATUS_LABEL: Record<CoverageVerdict['status'], string> = {
  covered: 'Covered',
  partial: 'Partial',
  over: 'Over',
};

export const CoveragePopover: React.FC<CoveragePopoverProps> = ({
  anchorElement,
  verdict,
  oldTranslatedValue,
  currentTranslatedValue,
  onUndo,
  onClose,
  onFixTranslation,
  isStale,
  onResolveRetranslate,
  onResolveManual,
  onHoverEnter,
  onHoverLeave,
  segmentId,
  splitControls,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Latest onClose for the mount-once native keydown trap below.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Placement comes from floating-ui, measured from the REAL rendered popover.
  // The previous hand-rolled version guessed a fixed 280px height; the popover
  // actually renders ~757px once a verdict has SUMMARY + MISSING + ADDED +
  // previous/current translation, so it opened downward and ran ~220px off the
  // bottom of the screen (measured, logs/trace-14 — Denys's 2026-07-14
  // screenshot). Nothing here estimates a height any more:
  //   flip  — chooses above/below from the measured rect
  //   shift — slides it back on-screen near an edge
  //   size  — caps max-height to the space actually available, so a long verdict
  //           SCROLLS inside the popover instead of being unreachable
  // Computed ONCE per open, deliberately WITHOUT `autoUpdate`: repositioning on
  // every scroll frame would fight the user scrolling the transcript beneath it,
  // which is why the original computed once too. That intent is preserved.
  const [placement, setPlacement] = useState<{
    left: number;
    top: number;
  } | null>(null);

  useLayoutEffect(() => {
    const floating = wrapperRef.current;
    if (!anchorElement || !floating) return;
    let cancelled = false;
    computePosition(anchorElement, floating, {
      strategy: 'fixed',
      placement: 'bottom-end',
      middleware: [
        offset(ANCHOR_GAP),
        flip({ padding: VIEWPORT_PADDING }),
        shift({ padding: VIEWPORT_PADDING }),
        size({
          padding: VIEWPORT_PADDING,
          apply({ availableHeight, elements }) {
            elements.floating.style.maxHeight = `${Math.max(
              MIN_POPOVER_HEIGHT,
              availableHeight,
            )}px`;
          },
        }),
      ],
    })
      .then(({ x, y }) => {
        if (!cancelled) setPlacement({ left: x, top: y });
      })
      .catch(() => {
        // Never leave the popover invisible if positioning fails: fall back to
        // the anchor's own corner rather than hiding the verdict entirely.
        if (cancelled) return;
        const r = anchorElement.getBoundingClientRect();
        setPlacement({ left: r.left, top: r.bottom + ANCHOR_GAP });
      });
    return () => {
      cancelled = true;
    };
  }, [anchorElement]);

  // Close on click-outside (same codepath as Escape / the close button).
  useOutsideAlerter(wrapperRef, onClose);

  // Native-level keydown trap on the wrapper: the transcript scroll container
  // and the workspace both listen for keys on ancestors (Space toggles
  // playback, Escape closes the whole workspace). Escape here closes only the
  // popover and stops the event so it never reaches the workspace's own
  // Escape-to-close. Mirrors `TranslatedPhraseEditorPopover`.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      e.stopPropagation();
    };
    const stop = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return;
      e.stopPropagation();
    };
    wrapper.addEventListener('keydown', onKeyDown);
    wrapper.addEventListener('keyup', stop);
    wrapper.addEventListener('keypress', stop);
    return () => {
      wrapper.removeEventListener('keydown', onKeyDown);
      wrapper.removeEventListener('keyup', stop);
      wrapper.removeEventListener('keypress', stop);
    };
  }, []);

  const color = bandColor(verdict.coveragePercent);
  // Read once — these strip leaked punctuation as well as gate the sections, so
  // the rendered text is the cleaned value, not the raw field.
  const explanation = readVerdictField(verdict.explanation);
  const missing = readVerdictField(verdict.missing);
  const added = readVerdictField(verdict.added);
  const showOld =
    oldTranslatedValue !== undefined &&
    oldTranslatedValue !== currentTranslatedValue;

  // PLAT-10268 (T8) — Atom 7, reshaped by PLAT-11335 into a single toggle. A
  // split segment merges back; a collapsed one splits, reusing a cached split
  // when there is one (free) rather than recomputing it (an endpoint call).
  const isSplit = splitControls.isSplit(segmentId);
  const canRedoSplit = splitControls.hasCachedSplit(segmentId) && !isSplit;

  return (
    <Wrapper
      ref={wrapperRef}
      placement={placement}
      data-testid="coverage-popover"
      onMouseDown={e => e.stopPropagation()}
      // The hover bridge: the pointer arriving here cancels the owner's pending
      // close, so travelling from the badge into the popover keeps it open and
      // its Undo / Split controls stay reachable.
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      role="dialog"
      aria-label="Coverage details"
    >
      {/* The verdict leads: the status word carries the meaning, the percent
          qualifies it. The previous order put a 16px number first and the word
          beside it at 12px/0.7 opacity, so the headline read as a bare score
          with no interpretation — which is how a wrong 100% came to look
          authoritative even while the summary underneath contradicted it. */}
      <Header>
        <Status style={{ color }}>{STATUS_LABEL[verdict.status]}</Status>
        <Percent>{verdict.coveragePercent}%</Percent>
      </Header>

      {/* Only this region scrolls, so the actions below stay reachable however
          long the analysis runs. */}
      <Content>
        {explanation && (
          <Summary data-testid="coverage-popover-explanation">
            {explanation}
          </Summary>
        )}

        {missing && (
          <Section>
            <SectionLabel>Missing</SectionLabel>
            <Prose data-testid="coverage-popover-missing">{missing}</Prose>
          </Section>
        )}

        {added && (
          <Section>
            <SectionLabel>Added</SectionLabel>
            <Prose data-testid="coverage-popover-added">{added}</Prose>
          </Section>
        )}

        {showOld && (
          <Section>
            <SectionLabel>Previous Translation</SectionLabel>
            <OriginalReference data-testid="coverage-popover-old">
              {oldTranslatedValue}
            </OriginalReference>
          </Section>
        )}

        <Section>
          <SectionLabel>Current Translation</SectionLabel>
          <CurrentValue data-testid="coverage-popover-current">
            {currentTranslatedValue}
          </CurrentValue>
        </Section>

        {/* PLAT-10266 (Card 4) — the SECOND home for the two resolve actions
            (DESIGN §7 "two homes"). Rendered ONLY for a stale fragment with at
            least one resolve handler wired; each appears only when its own
            handler is wired (mirrors `onFixTranslation` optionality). Entry
            wiring only — the host resolves the fragment, runs the resolve, and
            clears the amber. Existing call sites pass none of these, so the row
            never renders there (AC6 containment).

            These are contextual RESOLUTIONS of a stale fragment, not peers of
            the footer's actions, so they read as inline text actions under the
            condition that produced them rather than as two more buttons. */}
        {isStale && (onResolveRetranslate || onResolveManual) && (
          <Section data-testid="coverage-popover-resolve-row">
            <SectionLabel>The Source Text Changed</SectionLabel>
            <ResolveActions>
              {onResolveRetranslate && (
                <InlineTextAction
                  type="button"
                  onClick={onResolveRetranslate}
                  data-testid="coverage-popover-resolve-retranslate"
                >
                  Re-translate (machine)
                </InlineTextAction>
              )}
              {onResolveManual && (
                <InlineTextAction
                  type="button"
                  onClick={onResolveManual}
                  data-testid="coverage-popover-resolve-manual"
                >
                  Replace manually
                </InlineTextAction>
              )}
            </ResolveActions>
          </Section>
        )}
      </Content>

      <Footer>
        {/* PLAT-10257 (A4) — opens the shared two-mode span editor for this
            segment's covered words. Entry wiring only: the host resolves the
            indices + the overlap guard and closes this popover. */}
        {onFixTranslation && (
          <SecondaryButton
            type="button"
            onClick={onFixTranslation}
            data-testid="coverage-popover-fix-translation"
          >
            Fix Translation
          </SecondaryButton>
        )}
        <SecondaryButton
          type="button"
          onClick={onUndo}
          data-testid="coverage-popover-undo"
        >
          Undo
        </SecondaryButton>
        <Spacer />
        {/* PLAT-11335 — one toggle in place of Split / Undo split / Redo split.
            The three buttons were mutually disabling: at any moment exactly one
            could be pressed, so two of them were always dead weight in a 320px
            column. Splitting and re-applying a cached split are the same user
            intent ("split this segment"); which path runs is an implementation
            detail, so the toggle prefers the cache when one exists and falls
            back to the on-demand split. No capability is lost. */}
        <GhostButton
          type="button"
          onClick={() => {
            if (isSplit) splitControls.undoSplit(segmentId);
            else if (canRedoSplit) splitControls.redoSplit(segmentId);
            else splitControls.splitSegment(segmentId);
          }}
          data-testid="coverage-popover-split"
        >
          {isSplit ? 'Merge Segment' : 'Split Segment'}
        </GhostButton>
      </Footer>
    </Wrapper>
  );
};

const Wrapper = styled.div.attrs(
  (props: { placement: { left: number; top: number } | null }) => props,
)`
  position: fixed;
  left: ${p => p.placement?.left ?? 0}px;
  top: ${p => p.placement?.top ?? 0}px;
  /* floating-ui measures the RENDERED popover, so it must be in the DOM before
     it can be placed. Hide it for that one frame — otherwise it flashes in the
     top-left corner before jumping to the badge. */
  visibility: ${p => (p.placement ? 'visible' : 'hidden')};
  /* 360, not 320: at 320 a three-action footer (Fix Translation / Undo /
     Merge Segment) could not fit on one line and every multi-word label wrapped
     to two rows, which is what made the actions read as a jumbled block. */
  width: 360px;
  max-width: calc(100vw - 16px);
  /* The size() middleware writes an inline max-height from the space actually
     available; this is what turns "runs off the bottom of the screen" into
     "scrolls inside the popover". Without it the cap would just clip.
     box-sizing MATTERS here: size() grants a height for the WHOLE element, but
     under the default content-box a max-height of 550 plus 28px padding and 2px
     border renders 580 — measured, it still hung 22px off the bottom. border-box
     makes the cap mean what size() intended. */
  box-sizing: border-box;
  /* The WRAPPER no longer scrolls — the Content region below does. When the
     whole popover was the scroller, the max-height from size() cut the action
     row off the bottom and the reviewer had to scroll a hover-dismissed
     popover to reach Undo. Header and footer are now pinned, and only the
     analysis between them moves. */
  overflow: hidden;
  z-index: 100001;

  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  padding: 14px 16px;

  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex: 0 0 auto;
`;

/** The verdict word — the headline, carrying the band color. */
const Status = styled.span`
  font-weight: var(--font-weight-semi-bold);
  font-size: 15px;
  line-height: 1.2;
`;

/** The score, qualifying the word rather than replacing it. */
const Percent = styled.span`
  color: var(--foreground);
  opacity: 0.6;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`;

const Spacer = styled.div`
  flex: 1;
`;

/** The only scrolling region: header and footer stay put around it. */
const Content = styled.div`
  flex: 1 1 auto;
  /* Without an explicit min-height a flex item refuses to shrink below its
     content, so the cap would be ignored and the footer pushed out again. */
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding-top: 2px;
`;

/**
 * The segment-structure action. Deliberately lighter than the two translation
 * actions beside it: splitting/merging changes how the segment is CUT, not what
 * it says, so it should not compete with Fix Translation for attention. Ghost
 * tier per the design system (no border, muted text, neutral hover).
 */
const GhostButton = styled.button`
  cursor: pointer;
  padding: 6px 10px;
  border-radius: var(--radius-input);
  font-family: 'Inter', sans-serif;
  font-weight: var(--font-weight-medium);
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
  border: none;
  background: none;
  color: var(--text-muted);
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

/** The verdict's one-line reading, first thing under the header. */
const Summary = styled.div`
  color: var(--popover-foreground);
  line-height: 1.45;
`;

const ResolveActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  /* InlineTextAction carries its own 8px inset; pull the row back so the
     first action's text aligns with the label above it. */
  margin-left: -8px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Prose = styled.div`
  color: var(--popover-foreground);
  line-height: 1.4;
  opacity: 0.9;
`;

/** The live translated text — the one value that is not a reference. */
const CurrentValue = styled.div`
  color: var(--popover-foreground);
  line-height: 1.4;
  white-space: pre-wrap;
  user-select: text;
`;

const SecondaryButton = styled.button`
  cursor: pointer;
  /* nowrap: a two-word label ("Fix Translation") wrapped to two lines and made
     the footer buttons different heights. */
  white-space: nowrap;
  padding: 6px 12px;
  border-radius: var(--radius-input);
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--popover-foreground);
  transition:
    background 0.12s ease,
    border-color 0.12s ease;

  /* Neutral hover, per the design system's Secondary button rule — green
     (--primary) is reserved for actions, and a green wash on a row of
     equal-weight buttons reads as "this one is the primary". */
  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--border-elevated);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
